<script setup lang="ts">
import { tv } from 'tailwind-variants'

definePageMeta({
  name: 'product',
})

const route = useRoute<'product'>()

const slug = computed(() => route.params.slug)

const { data: product, suspense } = useProduct(slug)

await suspense()

if (!product.value) {
  const catalogue = useProducts()

  // FIXME: Not sure if this is allowed, as we have already suspended requests (L16).
  await catalogue.suspense()

  const fromCatalogue = catalogue.data.value?.find((it) => it.slug.short === slug.value || it.slug.custom === slug.value)

  if (fromCatalogue) {
    await navigateTo(`/produktkatalog/${fromCatalogue.slug.short}`, {
      redirectCode: 301,
    })
  } else {
    throw createError({
      statusCode: 404,
      statusMessage: 'Produktet finnes ikke',
      fatal: true,
    })
  }
}

const bodyClass = tv({
  base: '[--text:white]',
  variants: {
    theme: {
      'green-seagreen': 'bg-seagreen-40',
      'dark-red-pink': 'bg-red-50',
      'blue-pink': 'bg-blue-60',
      'green-red': 'bg-green-50',
      'green': 'bg-(--background) [--variant-background:177,219,208]',
      'purple': 'bg-(--background) [--variant-background:209,204,231]',
      'blue': 'bg-(--background) [--variant-background:204,227,236]',
    },
    template: {
      standard: '[--color:white]',
      pluss: '',
      grunnskole: '[--color:white]',
    },
  },
  compoundVariants: [
    {
      theme: 'green',
      template: ['standard', 'grunnskole'],
      class: [
        '[--background:var(--color-green-40)]',
        '[--foreground:var(--color-green-50)]',
        '[--accent-color:var(--color-green-30)]',
        '[--filter-background:#f5f9f8]',
        '[--filter-color:var(--color-green-50)]',
      ],
    },
    {
      theme: 'green',
      template: 'pluss',
      class: [
        '[--background:var(--color-green-20)]',
        '[--card-background:var(--color-green-10)]',
        '[--color:#015f40]',
      ],
    },
    {
      theme: 'blue',
      template: ['standard', 'grunnskole'],
      class: [
        '[--background:#01739f]',
        '[--foreground:#14475f]',
        '[--accent-color:var(--color-blue-30)]',
        '[--filter-background:#f1f8fb]',
        '[--filter-color:#14475f]',
      ],
    },
    {
      theme: 'blue',
      template: 'pluss',
      class: [
        '[--background:var(--color-blue-20)]',
        '[--card-background:var(--color-blue-10)]',
        '[--color:var(--color-blue-40)]',
      ],
    },
    {
      theme: 'purple',
      template: ['standard', 'grunnskole'],
      class: [
        '[--background:#65397c]',
        '[--foreground:#3e264d]',
        '[--accent-color:#9571a6]',
        '[--filter-background:#f3f2f9]',
        '[--filter-color:var(--color-purple-50)]',
      ],
    },
    {
      theme: 'purple',
      template: 'pluss',
      class: [
        '[--background:#ccd9ef]',
        '[--card-background:#eaf0f9]',
        '[--color:var(--color-blue-50)]',
      ],
    },
  ],
})

useHead({
  bodyAttrs: {
    class: computed(() => bodyClass({
      theme: product.value?.theme,
      template: product.value && 'template' in product.value ? product.value.template : undefined,
    })),
  },
})
</script>

<template>
  <div>
    <LayoutBreadCrumbs
      :background="product?.type === 'single-product' && product.template === 'pluss'"
      :text-conversions="{
        [slug]: product?.title || product?.name,
      }"
      class="bg-(--background)"
    />
    <UiPacket v-if="product?.type === 'packet'" :packet="product" />
    <UiSingleProduct
      v-else-if="product?.type === 'single-product'"
      :product="product"
    />
  </div>
</template>
